/**
 * Tendhouse motion layer.
 *
 * - <th-reveal>: reveals its [data-th-item] children once, the first time the group
 *   scrolls into view. Children stagger by their --th-i custom property. The hidden
 *   starting state lives in th-motion.css inside prefers-reduced-motion: no-preference,
 *   so nothing is hidden without this script or for visitors who prefer less motion.
 * - <th-steps>: the "set up in a weekend" scene. On wide screens the step crossing the
 *   middle of the viewport becomes active, and the sticky screen beside it follows.
 *   Without this script every step stays fully visible.
 *
 * Scrolling is left entirely to the browser. Native browser APIs only. Reveals stand
 * down for prefers-reduced-motion and inside the theme editor.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const designMode = Boolean(window.Shopify && window.Shopify.designMode);

class ThReveal extends HTMLElement {
  connectedCallback() {
    if (reducedMotion.matches || designMode || !('IntersectionObserver' in window)) {
      this.classList.add('is-in');
      return;
    }

    this.classList.add('is-armed');
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.classList.add('is-in');
          this.observer?.disconnect();
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
  }
}

class ThSteps extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.wide = window.matchMedia('(min-width: 990px)');
    this.wide.addEventListener('change', () => this.setup(), { signal: this.controller.signal });
    this.setup();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.observer = null;
  }

  setup() {
    this.observer?.disconnect();
    this.steps = [...this.querySelectorAll('[data-th-step]')];
    this.media = [...this.querySelectorAll('[data-th-step-media]')];

    if (!this.wide.matches || this.steps.length === 0 || !('IntersectionObserver' in window)) {
      this.classList.remove('is-ready');
      return;
    }

    this.classList.add('is-ready');
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.activate(this.steps.indexOf(entry.target));
        }
      },
      { rootMargin: '-48% 0px -48% 0px' }
    );
    for (const step of this.steps) this.observer.observe(step);
    this.activate(this.current ?? 0);
  }

  /** @param {number} index */
  activate(index) {
    if (index < 0) return;
    this.current = index;
    this.steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
    this.media.forEach((item, i) => item.classList.toggle('is-active', i === index));
    const progress = this.steps.length > 1 ? index / (this.steps.length - 1) : 1;
    this.style.setProperty('--th-progress', String(progress));
  }
}

if (!customElements.get('th-reveal')) customElements.define('th-reveal', ThReveal);
if (!customElements.get('th-steps')) customElements.define('th-steps', ThSteps);
